(() => {
  const splitLines = (value) =>
    (value || '')
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);

  const initSection = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const tabs = [...root.querySelectorAll('[data-apce-tab]')];
    const backgroundImage = root.querySelector('[data-apce-background-image]');
    const backgroundMobile = root.querySelector('[data-apce-background-mobile]');
    const productImage = root.querySelector('[data-apce-product-image]');
    const productMobile = root.querySelector('[data-apce-product-mobile]');
    const title = root.querySelector('[data-apce-title]');
    const eyebrow = root.querySelector('[data-apce-eyebrow]');
    const shipping = root.querySelector('[data-apce-shipping]');
    const giftDescription = root.querySelector('.athena-pro-collab-editions__gift-description');
    const features = root.querySelector('[data-apce-features]');
    const shopLink = root.querySelector('[data-apce-shop-link]');
    const featureSvgTemplates = features
      ? [...features.querySelectorAll('[data-apce-feature-icon] svg')].map((svg) => svg.outerHTML)
      : [];
    const isMobile = () => window.matchMedia?.('(max-width: 767px)').matches ?? false;
    let featureHideTimer = 0;

    const setImage = (image, url) => {
      if (!image) return;
      image.style.opacity = '0';
      window.setTimeout(() => {
        image.src = url || '';
        image.hidden = !url;
        image.style.opacity = '1';
      }, 80);
    };

    const setSource = (source, url) => {
      if (source) source.srcset = url || '';
    };

    const hideFeatureDetails = () => {
      if (!features) return;

      window.clearTimeout(featureHideTimer);
      features.querySelectorAll('[data-apce-feature-item]').forEach((item) => {
        const feature = item.querySelector('[data-apce-feature]');
        const detail = item.querySelector('[data-apce-detail-stack]');
        item.classList.remove('is-active');
        feature?.classList.remove('is-active');
        feature?.setAttribute('aria-expanded', 'false');
        if (detail) detail.hidden = true;
      });
    };

    const showFeatureDetails = (item) => {
      if (!features || !item) return;

      window.clearTimeout(featureHideTimer);
      const feature = item.querySelector('[data-apce-feature]');
      const detail = item.querySelector('[data-apce-detail-stack]');
      if (!feature || !detail) return;

      features.querySelectorAll('[data-apce-feature-item]').forEach((featureItem) => {
        const featureButton = featureItem.querySelector('[data-apce-feature]');
        const featureDetail = featureItem.querySelector('[data-apce-detail-stack]');
        const isActive = featureItem === item;
        featureItem.classList.toggle('is-active', isActive);
        featureButton?.classList.toggle('is-active', isActive);
        featureButton?.setAttribute('aria-expanded', String(isActive));
        if (featureDetail && featureDetail !== detail) featureDetail.hidden = true;
      });

      detail.hidden = false;
    };

    const scheduleFeatureDetailsHide = () => {
      window.clearTimeout(featureHideTimer);
      featureHideTimer = window.setTimeout(hideFeatureDetails, 140);
    };

    const updateFeatures = (tab) => {
      if (!features) return;

      hideFeatureDetails();
      features.innerHTML = '';
      const fallbackTitles = splitLines(tab.dataset.features || '');

      for (let index = 1; index <= 4; index += 1) {
        const item = document.createElement('li');
        const feature = document.createElement('button');
        const detailStack = document.createElement('div');
        const featureTitle =
          tab.getAttribute(`data-feature-${index}-title`) || fallbackTitles[index - 1] || '';
        const featureSubtitle = tab.getAttribute(`data-feature-${index}-subtitle`) || '';
        const featureDescription = tab.getAttribute(`data-feature-${index}-description`) || '';

        item.className = 'athena-pro-collab-editions__feature-item';
        item.dataset.apceFeatureItem = '';

        feature.type = 'button';
        feature.className = 'athena-pro-collab-editions__feature';
        feature.dataset.apceFeature = '';
        feature.dataset.featureIndex = String(index);
        feature.setAttribute('aria-label', featureTitle);
        feature.setAttribute('aria-expanded', 'false');
        feature.setAttribute('aria-controls', `${root.id}-feature-${index}`);
        feature.innerHTML = `
          <span class="athena-pro-collab-editions__feature-icon" data-apce-feature-icon aria-hidden="true">
            ${featureSvgTemplates[index - 1] || ''}
          </span>
          <span class="athena-pro-collab-editions__feature-name visually-hidden"></span>
        `;
        feature.querySelector('.athena-pro-collab-editions__feature-name').textContent = featureTitle;

        detailStack.id = `${root.id}-feature-${index}`;
        detailStack.className = 'athena-pro-collab-editions__detail-stack';
        detailStack.dataset.apceDetailStack = '';
        detailStack.hidden = true;
        detailStack.innerHTML = `
          <div class="athena-pro-collab-editions__detail">
            <div class="athena-pro-collab-editions__detail-title"></div>
            <div class="athena-pro-collab-editions__detail-subtitle"></div>
            <div class="athena-pro-collab-editions__detail-line"></div>
            <div class="athena-pro-collab-editions__detail-text"></div>
          </div>
        `;
        detailStack.querySelector('.athena-pro-collab-editions__detail-title').textContent = featureTitle;
        detailStack.querySelector('.athena-pro-collab-editions__detail-subtitle').textContent = featureSubtitle;
        detailStack.querySelector('.athena-pro-collab-editions__detail-text').textContent = featureDescription;

        item.append(feature, detailStack);
        features.appendChild(item);

        item.addEventListener('mouseenter', () => {
          if (!isMobile()) showFeatureDetails(item);
        });
        item.addEventListener('mouseleave', () => {
          if (!isMobile()) scheduleFeatureDetailsHide();
        });
        feature.addEventListener('focus', () => showFeatureDetails(item));
        feature.addEventListener('blur', () => {
          if (!isMobile()) scheduleFeatureDetailsHide();
        });
        feature.addEventListener('click', () => {
          if (isMobile() && feature.classList.contains('is-active')) {
            hideFeatureDetails();
            return;
          }
          showFeatureDetails(item);
        });
      }
    };

    const updateTab = (tab) => {
      if (!tab) return;

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', String(isActive));
      });

      const desktopBackground = tab.dataset.desktopBackground || '';
      const mobileBackground = tab.dataset.mobileBackground || desktopBackground;
      const desktopProduct = tab.dataset.desktopProduct || '';
      const mobileProduct = tab.dataset.mobileProduct || desktopProduct;
      const themeColor = tab.dataset.apceThemeColor || '';
      const themeIndex = tab.dataset.apceTabIndex || '';

      setImage(backgroundImage, desktopBackground);
      setSource(backgroundMobile, mobileBackground);
      setImage(productImage, desktopProduct);
      setSource(productMobile, mobileProduct);

      if (themeIndex) {
        root.dataset.apceThemeIndex = themeIndex;
      }

      if (themeColor) {
        root.style.setProperty('--apce-theme-color', themeColor);
        root.style.setProperty('--apce-feature-color', themeColor);
        shipping?.style.setProperty('color', themeColor, 'important');
        giftDescription?.style.setProperty('--apce-theme-color', themeColor);
        shopLink?.style.setProperty('background-color', themeColor, 'important');
      }

      if (title) title.textContent = tab.dataset.title || '';
      if (eyebrow) eyebrow.textContent = tab.dataset.eyebrow || '';
      if (shipping) shipping.textContent = tab.dataset.shipping || '';
      updateFeatures(tab);

      if (shopLink) {
        const productUrl = tab.dataset.productUrl || '#';
        const isDisabled = productUrl === '#';
        shopLink.href = productUrl;
        shopLink.classList.toggle('is-disabled', isDisabled);
        if (isDisabled) {
          shopLink.setAttribute('aria-disabled', 'true');
        } else {
          shopLink.removeAttribute('aria-disabled');
        }
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => updateTab(tab));
    });

    updateTab(tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0]);

    document.addEventListener('shopify:block:select', (event) => {
      if (!root.contains(event.target)) return;

      const blockId = event.target.closest?.('[data-apce-block-id]')?.dataset.apceBlockId;
      if (!blockId) return;

      updateTab(tabs.find((tab) => tab.dataset.apceBlockId === blockId));
    });
  };

  const initAll = () => {
    document.querySelectorAll('.athena-pro-collab-editions[data-section-id]').forEach(initSection);
  };

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', (event) => {
    const section =
      event.target.matches?.('.athena-pro-collab-editions[data-section-id]')
        ? event.target
        : event.target.querySelector?.('.athena-pro-collab-editions[data-section-id]');
    initSection(section);
  });
})();
